import type { RequiredGap } from '@seen/core/observation-logic/noteCapture'
import type { Editor } from '@tiptap/core'
import { useCallback, useRef, useState } from 'react'
import type { RequiredAnswersDialogProps } from '#src/components/rich-text-editor/required/RequiredAnswersDialog.tsx'
import { editorRequiredGaps } from '#src/components/rich-text-editor/required/requiredGaps.ts'
import {
  type RequiredAnswersSurface,
  reportRequiredAnswersBlocked,
  reportRequiredAnswersOverridden,
} from '#src/hooks/requiredAnswersObservability.ts'

export interface RequiredAnswersCheck {
  // Asks before a sign or submit when the note shows required questions
  // with no answer. Resolves `proceed` (with `override` when the clinician
  // chose to go ahead without them), or `answer` once they went back to the
  // note, where the gaps are outlined and the first is selected. `gaps`
  // overrides the editor's own, for gaps the server found.
  check: (
    gaps?: readonly RequiredGap[]
  ) => Promise<'proceed' | 'override' | 'answer'>
  // Render `<RequiredAnswersDialog {...dialog} />` beside the action.
  dialog: RequiredAnswersDialogProps
}

// The one way a note surface checks its required questions before signing
// or submitting. `getEditor` gives the note's editor, when one is mounted (a
// desk card signs without one, and passes the server's gaps to `check`).
export function useRequiredAnswers({
  getEditor,
  surface,
  action,
}: {
  getEditor?: () => Editor | null | undefined
  surface: RequiredAnswersSurface
  action: 'Sign' | 'Submit'
}): RequiredAnswersCheck {
  const [gaps, setGaps] = useState<readonly RequiredGap[]>([])
  const [open, setOpen] = useState(false)
  const settle = useRef<((result: 'override' | 'answer') => void) | null>(null)

  const check = useCallback(
    (given?: readonly RequiredGap[]) => {
      const editor = getEditor?.()
      const found =
        given ??
        (editor && !editor.isDestroyed ? editorRequiredGaps(editor) : [])
      if (!found.length) {
        return Promise.resolve('proceed' as const)
      }
      reportRequiredAnswersBlocked(surface, found)
      setGaps(found)
      setOpen(true)
      return new Promise<'override' | 'answer'>((resolve) => {
        settle.current = resolve
      })
    },
    [getEditor, surface]
  )

  const finish = (result: 'override' | 'answer'): void => {
    setOpen(false)
    settle.current?.(result)
    settle.current = null
    if (result === 'override') {
      reportRequiredAnswersOverridden(surface, gaps)
    } else {
      const editor = getEditor?.()
      if (editor && !editor.isDestroyed) {
        editor.chain().focus().showRequiredGaps().run()
      }
    }
  }

  return {
    check,
    dialog: {
      gaps,
      open,
      action,
      onAnswer: () => finish('answer'),
      onOverride: () => finish('override'),
    },
  }
}
